/**
 * CausticOverlay — fixed, pointer-events-none underwater light shimmer
 * layered behind all app content. Opacity is driven by the CSS custom
 * property `--caustic-intensity` (default 0.04) so it can be dialled
 * from Settings → Appearance without a code change.
 *
 * Behaviour:
 *   - Animated (`caustic-animated` class) under normal conditions.
 *   - Static (no animation) when `prefers-reduced-motion: reduce` is set.
 *   - On mount, reads the persisted `appearance.causticIntensity` value
 *     from the agent and applies it to `--caustic-intensity` on <html>
 *     so the saved preference takes effect immediately.
 */
import { useEffect } from 'react';
import { userDataApi } from '../lib/userdata-api';

/** Shape of the `appearance` settings namespace. */
interface AppearanceSettings {
  causticIntensity?: number;
}

/** Narrow unknown → AppearanceSettings without using `any`. */
function narrowAppearance(raw: unknown): AppearanceSettings {
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>;
    const causticIntensity =
      typeof obj.causticIntensity === 'number' ? obj.causticIntensity : undefined;
    return { causticIntensity };
  }
  return {};
}

export function CausticOverlay() {
  // Apply the persisted caustic intensity to the CSS var on mount.
  useEffect(() => {
    void (async () => {
      const result = await userDataApi.get('appearance');
      if (!result.ok) return;
      const { causticIntensity } = narrowAppearance(result.value.data);
      if (typeof causticIntensity === 'number') {
        document.documentElement.style.setProperty(
          '--caustic-intensity',
          String(causticIntensity),
        );
      }
    })();
  }, []);

  // Check motion preference once at render time. The component does not
  // subscribe to media-query changes — a page reload picks up new prefs.
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      aria-hidden="true"
      // caustic-animated applies the 40 s drift keyframe from tokens.css.
      // caustic-static falls through with no animation rule.
      className={prefersReducedMotion ? 'caustic-static' : 'caustic-animated'}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        // CSS custom property drives opacity so Settings can adjust live.
        opacity: 'var(--caustic-intensity)',
        overflow: 'hidden',
      }}
    >
      {/*
       * SVG feTurbulence creates the caustic light-patch pattern.
       * baseFrequency is intentionally low (large-scale patches) and
       * feColorMatrix tints output to a light blue-white, consistent
       * with natural underwater caustic light rather than green algae.
       */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        role="presentation"
      >
        <defs>
          <filter
            id="caustic-base"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="turbulence"
              baseFrequency="0.014 0.009"
              numOctaves="4"
              seed="12"
              result="noise"
            />
            <feColorMatrix
              type="matrix"
              in="noise"
              values="0 0 0 0 0.70
                      0 0 0 0 0.88
                      0 0 0 0 1.00
                      0 0 0 0.55 0"
            />
          </filter>
        </defs>
        <rect
          width="100%"
          height="100%"
          filter="url(#caustic-base)"
          fill="transparent"
        />
      </svg>
    </div>
  );
}
